import React from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, Select } from '../../components/ui';
import type { AiUsageChartMetric } from './AiUsageCharts';

export interface AiUsageProviderOption {
  id: string;
  label: string;
  requests: number;
}

export interface AiUsageFilterBarProps {
  periodDays: number;
  periodOptions: Array<{ value: string; label: string }>;
  onPeriodChange: (days: number) => void;
  chartMetric: AiUsageChartMetric;
  onChartMetricChange: (metric: AiUsageChartMetric) => void;
  selectedProvider: string | null;
  onProviderChange: (provider: string | null) => void;
  providerOptions: AiUsageProviderOption[];
  selectedFeature: string | null;
  onFeatureChange: (feature: string | null) => void;
  featureOptions: Array<{ value: string; label: string }>;
  selectedUserId: number | null;
  onUserChange: (userId: number | null) => void;
  userOptions: Array<{ value: string; label: string }>;
  onRefresh: () => void;
  onExport: () => void;
  exporting: boolean;
}

const PROVIDER_ACCENT: Record<string, string> = {
  openai: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  gemini: 'border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300',
  ollama: 'border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  openai_compatible: 'border-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300',
};

function FilterLabel({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <span className={cn('mb-1.5 block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
      {children}
    </span>
  );
}

const AiUsageFilterBar: React.FC<AiUsageFilterBarProps> = ({
  periodDays,
  periodOptions,
  onPeriodChange,
  chartMetric,
  onChartMetricChange,
  selectedProvider,
  onProviderChange,
  providerOptions,
  selectedFeature,
  onFeatureChange,
  featureOptions,
  selectedUserId,
  onUserChange,
  userOptions,
  onRefresh,
  onExport,
  exporting,
}) => {
  const { colors } = useTheme();

  const totalRequests = providerOptions.reduce((sum, p) => sum + p.requests, 0);

  return (
    <div className={cn('rounded-xl border', colors.border, colors.surface)}>
      <div className="border-b px-4 py-3 sm:px-5">
        <FilterLabel>Provider</FilterLabel>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onProviderChange(null)}
            aria-pressed={selectedProvider == null}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
              selectedProvider == null
                ? 'border-violet-500/50 bg-violet-500/15 text-violet-700 dark:text-violet-300'
                : cn(colors.border, colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5'),
            )}
          >
            All
            <span className="tabular-nums text-xs opacity-80">{totalRequests.toLocaleString()}</span>
          </button>
          {providerOptions.map((provider) => {
            const active = selectedProvider === provider.id;
            const accent = PROVIDER_ACCENT[provider.id];
            return (
              <button
                key={provider.id}
                type="button"
                onClick={() => onProviderChange(active ? null : provider.id)}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  active && accent
                    ? accent
                    : cn(colors.border, colors.text, 'hover:bg-black/5 dark:hover:bg-white/5'),
                  !active && provider.requests === 0 && 'opacity-50',
                )}
              >
                {provider.label}
                <span className="tabular-nums text-xs opacity-80">{provider.requests.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-end sm:px-5">
        <div className="min-w-[8rem] flex-1 sm:max-w-[10rem]">
          <FilterLabel>Period</FilterLabel>
          <Select
            name="ai-usage-period"
            value={String(periodDays)}
            onChange={(e) => onPeriodChange(Number(e.target.value))}
            options={periodOptions}
            showClearButton={false}
          />
        </div>
        <div className="min-w-[8rem] flex-1 sm:max-w-[12rem]">
          <FilterLabel>Feature</FilterLabel>
          <Select
            name="ai-usage-feature"
            value={selectedFeature ?? ''}
            onChange={(e) => onFeatureChange(e.target.value || null)}
            options={featureOptions}
            showClearButton={false}
          />
        </div>
        <div className="min-w-[10rem] flex-1 sm:max-w-[16rem]">
          <FilterLabel>User</FilterLabel>
          <Select
            name="ai-usage-user"
            value={selectedUserId != null ? String(selectedUserId) : ''}
            onChange={(e) => {
              const v = e.target.value;
              onUserChange(v ? Number(v) : null);
            }}
            options={userOptions}
            showClearButton={false}
            placeholder="All users"
          />
        </div>
        <div className="min-w-[8rem] flex-1 sm:max-w-[10rem]">
          <FilterLabel>Chart metric</FilterLabel>
          <Select
            name="ai-usage-chart-metric"
            value={chartMetric}
            onChange={(e) => onChartMetricChange(e.target.value as AiUsageChartMetric)}
            options={[
              { value: 'cost', label: 'Spend' },
              { value: 'requests', label: 'Requests' },
              { value: 'tokens', label: 'Tokens' },
            ]}
            showClearButton={false}
          />
        </div>
        <div className="flex shrink-0 gap-2 sm:ml-auto">
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onRefresh}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Refresh
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={onExport}
            disabled={exporting}
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiUsageFilterBar;
